import {
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Drawer,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure, useWindowScroll } from "@mantine/hooks";
import {
  type Icon,
  IconBrandWhatsapp,
  IconLayoutGrid,
  IconMessageCircle,
  IconQuote,
  IconTag,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { WHATSAPP_URL } from "$/data/links";

const links: Array<{ label: string; href: string; icon: Icon }> = [
  { label: "Funcionalidades", href: "/#features", icon: IconLayoutGrid },
  { label: "Depoimentos", href: "/#testimonials", icon: IconQuote },
  { label: "Preço", href: "/#pricing", icon: IconTag },
  { label: "Dúvidas", href: "/#faq", icon: IconMessageCircle },
];

const SECTION_IDS = ["features", "testimonials", "pricing", "faq"];

function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const offset = 120;

    const compute = () => {
      let current: string | null = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - offset <= 0 && rect.bottom - offset > 0) {
          current = id;
          break;
        }
      }
      setActive(current);
    };

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        compute();
        frame = 0;
      });
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ids]);

  return active;
}

export function Navbar() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [scroll] = useWindowScroll();
  const scrolled = scroll.y > 8;
  const activeId = useActiveSection(SECTION_IDS);

  return (
    <Box
      component="header"
      pos="fixed"
      top={0}
      left={0}
      right={0}
      className={scrolled ? "navbar-scrolled" : undefined}
      style={{
        zIndex: 100,
        background: scrolled ? "var(--mantine-color-white)" : "transparent",
        borderBottom: scrolled ? "1px solid var(--mantine-color-gray-2)" : "1px solid transparent",
        transition: "border-color 200ms ease, background-color 200ms ease",
      }}
    >
      <Container size="lg" py="md">
        <Group justify="space-between" pos="relative">
          <Anchor href="/" underline="never" c="dark.9">
            <Group gap="xs">
              <Image src="/logo.svg" w={32} h={32} alt="Verdana" />
              <Text fw={700} fz="lg" c="dark.9">
                Verdana
              </Text>
            </Group>
          </Anchor>

          <Group
            visibleFrom="lg"
            gap={32}
            pos="absolute"
            left="50%"
            style={{ transform: "translateX(-50%)" }}
          >
            {links.map((l) => {
              const isActive = activeId === l.href.split("#")[1];
              return (
                <Anchor
                  key={l.href}
                  href={l.href}
                  underline="never"
                  className={`nav-link ${isActive ? "is-active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  <l.icon size={16} stroke={1.8} />
                  <span>{l.label}</span>
                </Anchor>
              );
            })}
          </Group>

          <Button
            visibleFrom="lg"
            variant="gradient"
            gradient={{ from: "green.7", to: "green.5", deg: 135 }}
            leftSection={<IconBrandWhatsapp size={18} />}
            component="a"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener"
          >
            Quero conhecer
          </Button>

          <Burger opened={opened} onClick={toggle} hiddenFrom="lg" aria-label="Abrir menu" />
        </Group>
      </Container>

      <Drawer opened={opened} onClose={close} position="right" size="xs" title="Menu">
        <Stack gap="xs">
          {links.map((l) => {
            const isActive = activeId === l.href.split("#")[1];
            return (
              <Anchor
                key={l.href}
                href={l.href}
                onClick={close}
                underline="never"
                className={`nav-link nav-link-mobile ${isActive ? "is-active" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                <l.icon size={18} stroke={1.8} />
                <span>{l.label}</span>
              </Anchor>
            );
          })}
          <Button
            variant="gradient"
            gradient={{ from: "green.7", to: "green.5", deg: 135 }}
            leftSection={<IconBrandWhatsapp size={18} />}
            component="a"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener"
            onClick={close}
            mt="md"
          >
            Quero conhecer
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
